import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;
export const QUESTION_STATUS = {
  open: 'bg-amber-500/15 text-amber-300',
  answered: 'bg-teal-500/15 text-teal-300',
  resolved: 'bg-zinc-700 text-zinc-300',
};

export default function QuestionThread({ thread, token, onUpdated }) {
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/questions/${thread.question_id}/reply`, { body: reply }, { headers });
      setReply('');
      onUpdated?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send reply'); }
    finally { setBusy(false); }
  };

  const resolve = async () => {
    try {
      await axios.post(`${API}/questions/${thread.question_id}/resolve`, {}, { headers });
      toast.success('Marked as resolved');
      onUpdated?.({ ...thread, status: 'resolved' });
    } catch (e) { toast.error('Could not resolve'); }
  };

  return (
    <div className="space-y-4" data-testid={`question-thread-${thread.question_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white font-semibold">{thread.subject}</p>
          <p className="text-xs text-zinc-500 mt-0.5">{thread.learner_name}{thread.activity_title ? ` · ${thread.activity_title}` : ''}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs shrink-0 ${QUESTION_STATUS[thread.status]}`} data-testid="question-thread-status">{thread.status}</span>
      </div>
      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
        {thread.messages.map((m) => (
          <div key={m.message_id} className={`rounded-xl p-3 text-sm ${m.from_role === 'instructor' ? 'bg-teal-500/10 border border-teal-500/20 ml-6' : 'bg-zinc-800/60 border border-zinc-700/50 mr-6'}`}>
            <p className="text-xs text-zinc-400 mb-1">{m.author_name} · {m.from_role === 'instructor' ? 'Instructor' : 'Learner'} · {(m.at || '').slice(0, 16).replace('T', ' ')}</p>
            <p className="text-zinc-200 whitespace-pre-wrap">{m.body}</p>
          </div>
        ))}
      </div>
      {thread.status !== 'resolved' && (
        <div className="space-y-2">
          <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply..." className="bg-zinc-900 border-zinc-700 text-white min-h-[80px]" data-testid="question-reply-input" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={resolve} className="border-zinc-700 text-zinc-300" data-testid="question-resolve-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Mark resolved</Button>
            <Button size="sm" onClick={send} disabled={busy || !reply.trim()} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="question-reply-send-btn"><Send className="h-4 w-4 mr-1" />Send</Button>
          </div>
        </div>
      )}
    </div>
  );
}
